import {
  AmbientLight,
  Color,
  DirectionalLight,
  Float32BufferAttribute,
  Fog,
  HemisphereLight,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  PointLight,
  Scene,
} from "three";

import type { DevelopmentTileSet } from "../world/schema/tileSet.ts";
import type {
  RuntimeTileLodLevel,
  RuntimeWorldTilePayloadV2,
} from "../world/schema/runtimeTile.ts";
import type { WorldTileId } from "../world/schema/tile.ts";
import type { DistrictVisualSample } from "../world/schema/districtSample.ts";
import { MannequinView } from "./characterView/mannequin.ts";
import {
  RemotePlayerPopulationView,
  type RemotePlayerPresentation,
} from "./characterView/remotePlayers.ts";
import {
  createEnvironmentSceneController,
  type EnvironmentFrameState,
  type EnvironmentPresentation,
  type EnvironmentRenderConfig,
  type EnvironmentSceneController,
} from "./environment.ts";
import type { MannequinMotionConfig, RenderVector3 } from "./types.ts";
import {
  createDevelopmentTileSetView,
  type DevelopmentTileSetRenderDiagnostics,
  type DevelopmentTileSetView,
  type DevelopmentTileSetViewOptions,
} from "./worldView/developmentTileSetView.ts";
import {
  createStreamingWorldView,
  type RuntimeTileCameraObstruction,
  type RuntimeTileRenderMetrics,
  type RuntimeTileViewOptions,
  type StreamingWorldRenderDiagnostics,
  type StreamingWorldView,
} from "./worldView/runtimeTileView.ts";
import { BUILDING_RENDER_CONFIG } from "../config/buildings.ts";
import { VISUAL_REALISM_CONFIG } from "../config/visualRealism.ts";
import { createCityMaterialLibrary } from "./materials/cityMaterials.ts";
import {
  createDistrictSampleView,
  type DistrictSampleView,
} from "./worldView/districtSampleView.ts";
import {
  createThreeCrossesLandmark,
  type ThreeCrossesLandmark,
} from "./worldView/threeCrosses.ts";
import type { ResidentPresentation } from "../sim/neighborhood/index.ts";
import { NpcPopulationView } from "./npcView/population.ts";
import {
  loadVilniusLandmarks,
  type VilniusLandmarkCollection,
  type VilniusLandmarkDiagnostics,
  type VilniusLandmarkLoadOptions,
} from "./worldView/vilniusLandmarks.ts";
import {
  loadBoatFleetView,
  type BoatFleetDiagnostics,
  type BoatFleetView,
} from "./worldView/boatFleet.ts";
import type { BoatPresentation } from "../sim/boats/index.ts";
import {
  createInteriorLightingController,
  type InteriorLightingController,
  type InteriorLightingFrameState,
} from "./interiorLighting.ts";
import type { InteriorRenderPresentation } from "./types.ts";
import { createAtmosphericSkyDome } from "./sky.ts";
import {
  loadModularCityAssets,
  type ModularCityAssetCollection,
  type ModularCityAssetDiagnostics,
  type ModularCityAssetLoadOptions,
} from "./worldView/modularCityAssets.ts";
import { RoadVehicleViewController } from "./worldView/roadVehicleView.ts";
import {
  loadPublicTransportFleetView,
  type PublicTransportFleetDiagnostics,
  type PublicTransportFleetView,
} from "./worldView/publicTransportFleet.ts";
import type { PublicTransportVehiclePresentation } from
  "../sim/publicTransport/index.ts";
import { createStationInterchangeView } from "./worldView/stationInterchange.ts";
import { CHARACTER_PRESENTATION_CONFIG } from "../config/character.ts";
import { VILNIUS_AUTHORED_ASSET_SECONDARY_STREAM_DELAY_MS } from "../config/landmarks.ts";

export interface SceneStage {
  readonly scene: Scene;
  readonly mannequin: MannequinView;
  setRemotePlayerPresentations(
    presentations: readonly Readonly<RemotePlayerPresentation>[],
  ): void;
  updateRemotePlayers(interpolationAlpha: number, animationTimeSeconds: number): void;
  setEnvironment(presentation: Readonly<EnvironmentPresentation>): void;
  scrubEnvironment(deltaHours: number): void;
  toggleEnvironmentFrozen(): boolean;
  updateEnvironment(
    deltaSeconds: number,
    focus: Readonly<RenderVector3>,
  ): EnvironmentFrameState;
  setDirectionalShadowsEnabled(enabled: boolean): void;
  setAerialVisibilityEnabled(enabled: boolean): void;
  setFogFarDayMeters(
    groundedFogFarDayMeters: number,
    flightFogFarDayMeters: number,
  ): void;
  getEnvironmentState(): EnvironmentFrameState;
  setInteriorPresentation(
    presentation: Readonly<InteriorRenderPresentation>,
  ): void;
  getInteriorLightingState(): InteriorLightingFrameState;
  setDevelopmentTileSet(
    tileSet: Readonly<DevelopmentTileSet>,
    options?: Readonly<DevelopmentTileSetViewOptions>,
  ): DevelopmentTileSetRenderDiagnostics;
  clearDevelopmentTileSet(): void;
  attachWorldTile(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
    options?: Readonly<RuntimeTileViewOptions>,
  ): RuntimeTileRenderMetrics;
  attachWorldTileInPhases(
    tile: Readonly<RuntimeWorldTilePayloadV2>,
    options: Readonly<RuntimeTileViewOptions>,
    yieldToNextFrame: () => Promise<void>,
    shouldContinue: () => boolean,
  ): Promise<RuntimeTileRenderMetrics | null>;
  updateWorldTile(
    tileId: WorldTileId,
    options: Readonly<RuntimeTileViewOptions>,
  ): RuntimeTileRenderMetrics;
  detachWorldTile(tileId: WorldTileId): boolean;
  setWorldTileLod(
    tileId: WorldTileId,
    lod: RuntimeTileLodLevel,
  ): RuntimeTileRenderMetrics;
  getWorldRenderDiagnostics(): StreamingWorldRenderDiagnostics;
  getWorldTileCameraObstructions(
    tileId: WorldTileId,
  ): readonly RuntimeTileCameraObstruction[];
  clearWorldTiles(): void;
  loadVilniusLandmarks(
    manifestUrl: string,
    options?: Readonly<VilniusLandmarkLoadOptions>,
  ): Promise<VilniusLandmarkDiagnostics>;
  loadVilniusBridges(
    manifestUrl: string,
    options?: Readonly<VilniusLandmarkLoadOptions>,
  ): Promise<VilniusLandmarkDiagnostics>;
  loadParkedVehicles(
    manifestUrl: string,
    options?: Readonly<VilniusLandmarkLoadOptions>,
  ): Promise<VilniusLandmarkDiagnostics>;
  loadCivicSquares(
    manifestUrl: string,
    options?: Readonly<VilniusLandmarkLoadOptions>,
  ): Promise<VilniusLandmarkDiagnostics>;
  loadVilniusAirport(
    manifestUrl: string,
    options?: Readonly<VilniusLandmarkLoadOptions>,
  ): Promise<VilniusLandmarkDiagnostics>;
  loadTraversalNetwork(
    manifestUrl: string,
    options?: Readonly<VilniusLandmarkLoadOptions>,
  ): Promise<VilniusLandmarkDiagnostics>;
  loadModularCityAssets(
    manifestUrl: string,
    options?: Readonly<ModularCityAssetLoadOptions>,
  ): Promise<ModularCityAssetDiagnostics>;
  startDeferredAuthoredAssetStreaming(): void;
  loadDeferredAuthoredAssets(): Promise<void>;
  setTraversalShortcutState(access: Readonly<{
    blueGate: boolean;
    rooftopShortcut: boolean;
  }>): void;
  getLandmarkCameraObstructions(): readonly RuntimeTileCameraObstruction[];
  setDistrictVisualSample(
    sample: Readonly<DistrictVisualSample> | null,
    viewpointWorldPosition?: Readonly<RenderVector3>,
  ): number | null;
  setThreeCrossesLandmark(position: Readonly<RenderVector3> | null): void;
  setResidentPresentations(
    presentations: readonly Readonly<ResidentPresentation>[],
    animationTimeSeconds?: number,
  ): void;
  loadBoatFleet(manifestUrl: string): Promise<BoatFleetDiagnostics>;
  setBoatPresentations(
    presentations: readonly Readonly<BoatPresentation>[],
  ): void;
  setRoadVehiclePresentations(
    presentations: readonly Readonly<import("../sim/vehicles/index.ts").RoadVehiclePresentation>[],
  ): void;
  loadPublicTransportFleet(): Promise<PublicTransportFleetDiagnostics>;
  setPublicTransportPresentations(
    presentations: readonly Readonly<PublicTransportVehiclePresentation>[],
  ): void;
  dispose(): void;
}

export interface SceneStageOptions {
  readonly visualRealismEnabled?: boolean;
}

const FIELD_SIZE_METERS = 240;
const FIELD_SEGMENTS = 48;

function createCalibrationField(): Mesh<PlaneGeometry, MeshStandardMaterial> {
  const geometry = new PlaneGeometry(
    FIELD_SIZE_METERS,
    FIELD_SIZE_METERS,
    FIELD_SEGMENTS,
    FIELD_SEGMENTS
  );
  geometry.rotateX(-Math.PI * 0.5);

  const position = geometry.getAttribute("position");
  const colors: number[] = [];
  const baseColor = new Color(0x6f7465);

  for (let index = 0; index < position.count; index += 1) {
    const x = position.getX(index);
    const z = position.getZ(index);
    const broadVariation = Math.sin(x * 0.075) * Math.cos(z * 0.06) * 0.018;
    const edgeFalloff = Math.min(1, Math.hypot(x, z) / (FIELD_SIZE_METERS * 0.7));
    const vertexColor = baseColor.clone().offsetHSL(
      broadVariation * 0.08,
      broadVariation,
      broadVariation - edgeFalloff * 0.025
    );
    colors.push(vertexColor.r, vertexColor.g, vertexColor.b);
  }

  geometry.setAttribute("color", new Float32BufferAttribute(colors, 3));

  const material = new MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    roughness: 0.98,
    metalness: 0
  });

  const field = new Mesh(geometry, material);
  field.name = "calibration-field";
  field.receiveShadow = true;
  return field;
}

export function createSceneStage(
  shadowMapSize: number,
  environmentConfig: Readonly<EnvironmentRenderConfig>,
  mannequinMotionConfig?: Readonly<MannequinMotionConfig>,
  options: Readonly<SceneStageOptions> = {},
): SceneStage {
  const scene = new Scene();
  const skyColor = new Color(0xa7afa8);
  scene.background = skyColor;
  scene.fog = new Fog(0xa7afa8, 120, 680);
  const skyDome = createAtmosphericSkyDome(environmentConfig.atmosphere);
  scene.add(skyDome.object);

  const field = createCalibrationField();
  scene.add(field);

  const hemisphere = new HemisphereLight(0xc7d0ca, 0x454a42, 1.55);
  hemisphere.name = "soft-sky-light";
  scene.add(hemisphere);

  const direction = new DirectionalLight(0xf0dfbd, 2.8);
  direction.name = "late-afternoon-direction";
  direction.position.set(-16, 25, -11);
  direction.castShadow = true;
  direction.shadow.mapSize.set(shadowMapSize, shadowMapSize);
  direction.shadow.camera.left = -48;
  direction.shadow.camera.right = 48;
  direction.shadow.camera.top = 48;
  direction.shadow.camera.bottom = -48;
  direction.shadow.camera.near = 10;
  direction.shadow.camera.far = 320;
  direction.shadow.bias = -0.00025;
  direction.shadow.normalBias = 0.04;
  direction.shadow.radius = 2;
  direction.shadow.blurSamples = 8;
  scene.add(direction);
  scene.add(direction.target);

  const fill = new AmbientLight(0x718079, 0.32);
  fill.name = "soft-fill";
  scene.add(fill);

  const characterFill = new PointLight(
    CHARACTER_PRESENTATION_CONFIG.nightFill.colour,
    0,
    CHARACTER_PRESENTATION_CONFIG.nightFill.distanceMeters,
    2,
  );
  characterFill.name = "player-night-fill";
  characterFill.castShadow = false;
  scene.add(characterFill);

  const mannequin = new MannequinView(mannequinMotionConfig);
  scene.add(mannequin.object);
  const remotePlayers = new RemotePlayerPopulationView(mannequinMotionConfig);
  scene.add(remotePlayers.object);
  const population = new NpcPopulationView();
  scene.add(population.object);
  const cityMaterials = createCityMaterialLibrary({
    visualRealismEnabled:
      options.visualRealismEnabled ?? VISUAL_REALISM_CONFIG.enabledByDefault,
  });
  const stationInterchange = createStationInterchangeView(cityMaterials);
  scene.add(stationInterchange.object);
  const environment: EnvironmentSceneController =
    createEnvironmentSceneController(
      scene,
      hemisphere,
      direction,
      fill,
      environmentConfig,
      (frame) => {
        cityMaterials.setNightFactor(frame.nightFactor);
        cityMaterials.setSurfaceWetness(frame.surfaceWetness01);
      },
    );
  const interiorLighting: InteriorLightingController =
    createInteriorLightingController(
      scene,
      hemisphere,
      direction,
      fill,
    );

  let developmentWorld: DevelopmentTileSetView | null = null;
  const streamingWorld: StreamingWorldView = createStreamingWorldView(
    cityMaterials,
    BUILDING_RENDER_CONFIG,
  );
  scene.add(streamingWorld.object);
  let districtSample: DistrictSampleView | null = null;
  let threeCrosses: ThreeCrossesLandmark | null = null;
  let vilniusLandmarks: VilniusLandmarkCollection | null = null;
  let vilniusBridges: VilniusLandmarkCollection | null = null;
  let parkedVehicles: VilniusLandmarkCollection | null = null;
  const roadVehicleView = new RoadVehicleViewController();
  let civicSquares: VilniusLandmarkCollection | null = null;
  let vilniusAirport: VilniusLandmarkCollection | null = null;
  let traversalNetwork: VilniusLandmarkCollection | null = null;
  let modularCityAssets: ModularCityAssetCollection | null = null;
  let boatFleet: BoatFleetView | null = null;
  let deferredAuthoredAssetStreamingStarted = false;
  let secondaryAuthoredAssetStreamingTimer: ReturnType<typeof setTimeout> | null = null;
  let publicTransportFleet: PublicTransportFleetView | null = null;
  let directionalShadowsEnabled = true;
  let currentSeason: EnvironmentPresentation["season"] = "summer";

  function setAuthoredCollectionsSeason(
    season: EnvironmentPresentation["season"],
  ): void {
    for (const collection of [
      vilniusLandmarks,
      vilniusBridges,
      parkedVehicles,
      civicSquares,
      vilniusAirport,
      traversalNetwork,
      modularCityAssets,
    ]) {
      collection?.setSeason(season);
    }
  }

  function clearDevelopmentTileSet(): void {
    if (developmentWorld === null) {
      return;
    }
    developmentWorld.dispose();
    developmentWorld = null;
    if (streamingWorld.getDiagnostics().attachedTileCount === 0) {
      scene.add(field);
    }
  }

  return {
    scene,
    mannequin,
    setRemotePlayerPresentations(presentations): void {
      remotePlayers.setPresentations(presentations);
    },
    updateRemotePlayers(interpolationAlpha, animationTimeSeconds): void {
      remotePlayers.update(interpolationAlpha, animationTimeSeconds);
    },
    setEnvironment(presentation): void {
      if (presentation.season !== currentSeason) {
        currentSeason = presentation.season;
        cityMaterials.setSeason(currentSeason);
        setAuthoredCollectionsSeason(currentSeason);
      }
      environment.set(presentation);
    },
    scrubEnvironment(deltaHours): void {
      environment.scrub(deltaHours);
    },
    toggleEnvironmentFrozen(): boolean {
      return environment.toggleFrozen();
    },
    updateEnvironment(deltaSeconds, focus): EnvironmentFrameState {
      cityMaterials.advanceWaterTime(deltaSeconds);
      cityMaterials.advanceTrafficSignalTime(deltaSeconds);
      const frame = environment.update(deltaSeconds, focus);
      characterFill.position.set(
        focus.x - 1.1,
        focus.y + 2.25,
        focus.z - 1.35,
      );
      characterFill.intensity =
        frame.duskFactor * CHARACTER_PRESENTATION_CONFIG.nightFill.duskIntensity +
        frame.nightFactor * CHARACTER_PRESENTATION_CONFIG.nightFill.nightIntensity;
      modularCityAssets?.update(focus, frame.fogFarMeters);
      for (const collection of [
        vilniusLandmarks,
        vilniusBridges,
        parkedVehicles,
        civicSquares,
        vilniusAirport,
        traversalNetwork,
      ]) {
        collection?.updateVisibility(focus, frame.fogFarMeters);
      }
      const interiorFrame = interiorLighting.update(deltaSeconds, focus);
      const zenith = scene.background instanceof Color
        ? scene.background
        : skyColor;
      const horizon = scene.fog instanceof Fog
        ? scene.fog.color
        : zenith;
      skyDome.update(focus, frame, zenith, horizon, direction.position);
      return {
        ...frame,
        exposure: frame.exposure * interiorFrame.exposureScale,
      };
    },
    setDirectionalShadowsEnabled(enabled): void {
      if (enabled === directionalShadowsEnabled) {
        return;
      }
      directionalShadowsEnabled = enabled;
      direction.castShadow = enabled;
      direction.shadow.needsUpdate = true;
    },
    setAerialVisibilityEnabled(enabled): void {
      environment.setAerialVisibilityEnabled(enabled);
    },
    setFogFarDayMeters(
      groundedFogFarDayMeters,
      flightFogFarDayMeters,
    ): void {
      environment.setFogFarDayMeters(
        groundedFogFarDayMeters,
        flightFogFarDayMeters,
      );
    },
    getEnvironmentState(): EnvironmentFrameState {
      return environment.getState();
    },
    setInteriorPresentation(presentation): void {
      interiorLighting.setActiveInterior(presentation.activeInteriorId);
    },
    getInteriorLightingState(): InteriorLightingFrameState {
      return interiorLighting.getState();
    },
    setDevelopmentTileSet(
      tileSet: Readonly<DevelopmentTileSet>,
      options: Readonly<DevelopmentTileSetViewOptions> = {},
    ): DevelopmentTileSetRenderDiagnostics {
      if (developmentWorld !== null) {
        developmentWorld.dispose();
      }
      streamingWorld.clear();
      field.removeFromParent();
      developmentWorld = createDevelopmentTileSetView(tileSet, options);
      scene.add(developmentWorld.object);
      return developmentWorld.diagnostics;
    },
    clearDevelopmentTileSet,
    attachWorldTile(
      tile: Readonly<RuntimeWorldTilePayloadV2>,
      options: Readonly<RuntimeTileViewOptions> = {},
    ): RuntimeTileRenderMetrics {
      if (developmentWorld !== null) {
        developmentWorld.dispose();
        developmentWorld = null;
      }
      field.removeFromParent();
      return streamingWorld.attach(tile, options);
    },
    async attachWorldTileInPhases(
      tile,
      options,
      yieldToNextFrame,
      shouldContinue,
    ): Promise<RuntimeTileRenderMetrics | null> {
      if (developmentWorld !== null) {
        developmentWorld.dispose();
        developmentWorld = null;
      }
      field.removeFromParent();
      return streamingWorld.attachInPhases(
        tile,
        options,
        yieldToNextFrame,
        shouldContinue,
      );
    },
    updateWorldTile(
      tileId: WorldTileId,
      options: Readonly<RuntimeTileViewOptions>,
    ): RuntimeTileRenderMetrics {
      return streamingWorld.update(tileId, options);
    },
    detachWorldTile(tileId: WorldTileId): boolean {
      const detached = streamingWorld.detach(tileId);
      if (
        detached &&
        streamingWorld.getDiagnostics().attachedTileCount === 0 &&
        developmentWorld === null
      ) {
        scene.add(field);
      }
      return detached;
    },
    setWorldTileLod(
      tileId: WorldTileId,
      lod: RuntimeTileLodLevel,
    ): RuntimeTileRenderMetrics {
      return streamingWorld.update(tileId, { lod });
    },
    getWorldRenderDiagnostics(): StreamingWorldRenderDiagnostics {
      return streamingWorld.getDiagnostics();
    },
    getWorldTileCameraObstructions(
      tileId: WorldTileId,
    ): readonly RuntimeTileCameraObstruction[] {
      return streamingWorld.getCameraObstructions(tileId);
    },
    clearWorldTiles(): void {
      streamingWorld.clear();
      if (developmentWorld === null) {
        scene.add(field);
      }
    },
    async loadVilniusLandmarks(
      manifestUrl,
      options,
    ): Promise<VilniusLandmarkDiagnostics> {
      vilniusLandmarks?.dispose();
      vilniusLandmarks = await loadVilniusLandmarks(
        manifestUrl,
        fetch,
        options,
      );
      vilniusLandmarks.setSeason(currentSeason);
      vilniusLandmarks.object.visible = districtSample === null;
      scene.add(vilniusLandmarks.object);
      return vilniusLandmarks.diagnostics;
    },
    async loadVilniusBridges(
      manifestUrl,
      options,
    ): Promise<VilniusLandmarkDiagnostics> {
      vilniusBridges?.dispose();
      vilniusBridges = await loadVilniusLandmarks(manifestUrl, fetch, options);
      vilniusBridges.setSeason(currentSeason);
      vilniusBridges.object.name = "vilnius-authored-bridges";
      vilniusBridges.object.visible = districtSample === null;
      scene.add(vilniusBridges.object);
      return vilniusBridges.diagnostics;
    },
    async loadParkedVehicles(
      manifestUrl,
      options,
    ): Promise<VilniusLandmarkDiagnostics> {
      parkedVehicles?.dispose();
      roadVehicleView.clear();
      parkedVehicles = await loadVilniusLandmarks(manifestUrl, fetch, options);
      parkedVehicles.setSeason(currentSeason);
      parkedVehicles.object.name = "vilnius-parked-vehicles";
      parkedVehicles.object.visible = districtSample === null;
      scene.add(parkedVehicles.object);
      return parkedVehicles.diagnostics;
    },
    async loadCivicSquares(
      manifestUrl,
      options,
    ): Promise<VilniusLandmarkDiagnostics> {
      civicSquares?.dispose();
      civicSquares = await loadVilniusLandmarks(manifestUrl, fetch, options);
      civicSquares.setSeason(currentSeason);
      civicSquares.object.name = "vilnius-authored-civic-squares";
      civicSquares.object.visible = districtSample === null;
      scene.add(civicSquares.object);
      return civicSquares.diagnostics;
    },
    async loadVilniusAirport(
      manifestUrl,
      options,
    ): Promise<VilniusLandmarkDiagnostics> {
      vilniusAirport?.dispose();
      vilniusAirport = await loadVilniusLandmarks(manifestUrl, fetch, options);
      vilniusAirport.setSeason(currentSeason);
      vilniusAirport.object.name = "vilnius-authored-airport";
      vilniusAirport.object.visible = districtSample === null;
      scene.add(vilniusAirport.object);
      return vilniusAirport.diagnostics;
    },
    async loadTraversalNetwork(
      manifestUrl,
      options,
    ): Promise<VilniusLandmarkDiagnostics> {
      traversalNetwork?.dispose();
      traversalNetwork = await loadVilniusLandmarks(manifestUrl, fetch, options);
      traversalNetwork.setSeason(currentSeason);
      traversalNetwork.object.name = "vilnius-traversal-network";
      traversalNetwork.object.visible = districtSample === null;
      scene.add(traversalNetwork.object);
      return traversalNetwork.diagnostics;
    },
    async loadModularCityAssets(
      manifestUrl,
      options,
    ): Promise<ModularCityAssetDiagnostics> {
      modularCityAssets?.dispose();
      modularCityAssets = await loadModularCityAssets(
        manifestUrl,
        fetch,
        options,
      );
      modularCityAssets.setSeason(currentSeason);
      modularCityAssets.object.visible = districtSample === null;
      scene.add(modularCityAssets.object);
      return modularCityAssets.diagnostics;
    },
    async loadDeferredAuthoredAssets(): Promise<void> {
      for (const collection of [
        vilniusLandmarks,
        vilniusBridges,
        parkedVehicles,
        civicSquares,
        vilniusAirport,
        traversalNetwork,
        modularCityAssets,
      ]) {
        await collection?.loadDeferred();
      }
    },
    startDeferredAuthoredAssetStreaming(): void {
      if (deferredAuthoredAssetStreamingStarted) return;
      deferredAuthoredAssetStreamingStarted = true;
      // Primary landmarks claim the shared idle-time queue first. Secondary
      // collections and nearby modular full detail follow only after the
      // player has had time to enter and settle in the city.
      vilniusLandmarks?.startDeferredStreaming();
      secondaryAuthoredAssetStreamingTimer = setTimeout(() => {
        secondaryAuthoredAssetStreamingTimer = null;
        for (const collection of [
          vilniusBridges,
          parkedVehicles,
          civicSquares,
          vilniusAirport,
          traversalNetwork,
          modularCityAssets,
        ]) {
          collection?.startDeferredStreaming();
        }
      }, VILNIUS_AUTHORED_ASSET_SECONDARY_STREAM_DELAY_MS);
    },
    setTraversalShortcutState(access): void {
      const open = Math.PI * 0.48;
      for (const [id, offset] of [
        ["courtyard-blue-gate-left", access.blueGate ? open : 0],
        ["courtyard-blue-gate-right", access.blueGate ? -open : 0],
        ["courtyard-roof-gate-left", access.rooftopShortcut ? open : 0],
        ["courtyard-roof-gate-right", access.rooftopShortcut ? -open : 0],
      ] as const) {
        traversalNetwork?.setLandmarkYawOffset(id, offset);
      }
    },
    getLandmarkCameraObstructions(): readonly RuntimeTileCameraObstruction[] {
      return vilniusLandmarks?.cameraObstructions ?? [];
    },
    setDistrictVisualSample(
      sample,
      viewpointWorldPosition,
    ): number | null {
      districtSample?.dispose();
      districtSample = null;
      streamingWorld.object.visible = sample === null;
      if (vilniusLandmarks !== null) {
        vilniusLandmarks.object.visible = sample === null;
      }
      if (vilniusBridges !== null) {
        vilniusBridges.object.visible = sample === null;
      }
      if (parkedVehicles !== null) {
        parkedVehicles.object.visible = sample === null;
      }
      if (civicSquares !== null) {
        civicSquares.object.visible = sample === null;
      }
      if (vilniusAirport !== null) {
        vilniusAirport.object.visible = sample === null;
      }
      if (traversalNetwork !== null) {
        traversalNetwork.object.visible = sample === null;
      }
      if (modularCityAssets !== null) {
        modularCityAssets.object.visible = sample === null;
      }
      if (boatFleet !== null) {
        boatFleet.object.visible = sample === null;
      }
      if (publicTransportFleet !== null) {
        publicTransportFleet.object.visible = sample === null;
      }
      if (sample === null) {
        return null;
      }
      if (viewpointWorldPosition === undefined) {
        throw new Error(
          "District style study requires a world-space viewpoint anchor",
        );
      }
      districtSample = createDistrictSampleView(
        sample,
        cityMaterials,
        BUILDING_RENDER_CONFIG,
        viewpointWorldPosition,
      );
      scene.add(districtSample.object);
      return districtSample.facingYaw;
    },
    setThreeCrossesLandmark(position): void {
      threeCrosses?.dispose();
      threeCrosses = null;
      if (position !== null) {
        threeCrosses = createThreeCrossesLandmark(position);
        scene.add(threeCrosses.object);
      }
    },
    setResidentPresentations(presentations, animationTimeSeconds): void {
      population.setPresentations(presentations, animationTimeSeconds);
    },
    async loadBoatFleet(manifestUrl): Promise<BoatFleetDiagnostics> {
      boatFleet?.dispose();
      boatFleet = await loadBoatFleetView(manifestUrl);
      boatFleet.object.visible = districtSample === null;
      scene.add(boatFleet.object);
      return boatFleet.diagnostics;
    },
    setBoatPresentations(presentations): void {
      boatFleet?.setPresentations(presentations);
    },
    setRoadVehiclePresentations(presentations): void {
      for (const vehicle of presentations) {
        roadVehicleView.setPresentation(
          vehicle,
          parkedVehicles?.getLandmarkObject(vehicle.id) ?? null,
        );
      }
    },
    async loadPublicTransportFleet(): Promise<PublicTransportFleetDiagnostics> {
      publicTransportFleet?.dispose();
      publicTransportFleet = await loadPublicTransportFleetView();
      publicTransportFleet.object.visible = districtSample === null;
      scene.add(publicTransportFleet.object);
      return publicTransportFleet.diagnostics;
    },
    setPublicTransportPresentations(presentations): void {
      publicTransportFleet?.setPresentations(presentations);
    },
    dispose(): void {
      if (secondaryAuthoredAssetStreamingTimer !== null) {
        clearTimeout(secondaryAuthoredAssetStreamingTimer);
        secondaryAuthoredAssetStreamingTimer = null;
      }
      if (developmentWorld !== null) {
        developmentWorld.dispose();
        developmentWorld = null;
      }
      streamingWorld.dispose();
      districtSample?.dispose();
      threeCrosses?.dispose();
      vilniusLandmarks?.dispose();
      vilniusBridges?.dispose();
      parkedVehicles?.dispose();
      roadVehicleView.clear();
      civicSquares?.dispose();
      vilniusAirport?.dispose();
      traversalNetwork?.dispose();
      modularCityAssets?.dispose();
      boatFleet?.dispose();
      publicTransportFleet?.dispose();
      stationInterchange.dispose();
      cityMaterials.dispose();
      interiorLighting.dispose();
      environment.dispose();
      skyDome.dispose();
      mannequin.dispose();
      remotePlayers.dispose();
      population.dispose();
      field.geometry.dispose();
      field.material.dispose();
      scene.clear();
    }
  };
}
